import React, { ReactElement, SFC, MouseEvent } from 'react';
import styled from 'styled-components';
import { forDesktop, forIE } from '@westwing/ui-kit/breakPoints';
import useTranslator from '../../../hooks/useTranslator';
import Svg from '../../Generic/Icons/index';
import { SortingParameters, ProductSorting } from '../../../redux/products/types';
import { Actions } from '../../../ga/types';
import useTrackingPageLabel from '../../../hooks/useTrackingPageLabel';
import useGa from '../../../hooks/useGa';
import useRouter from '../../../hooks/useRouter';
import { StyledTextSpan } from '../../Generic/Text/StyledText';
import SortBox from './SortBox';
import DesktopFiltersStrip from './DesktopFiltersStrip';

export enum SORT_KEYS {
    BOOST_SORT_KEY = 'score_product_boost-desc',
    NOVELTY_SORT_KEY = 'score_novelty-desc',
    PRICE_DESC_SORT_KEY = 'facet_price-desc',
    PRICE_ASC_SORT_KEY = 'facet_price-asc',
}

const sortNameToGALabelMap = {
    [SORT_KEYS.BOOST_SORT_KEY]: Actions.Filters.SortingActivatedFav,
    [SORT_KEYS.NOVELTY_SORT_KEY]: Actions.Filters.SortingActivatedNew,
    [SORT_KEYS.PRICE_DESC_SORT_KEY]: Actions.Filters.SortingActivatedPriceDesc,
    [SORT_KEYS.PRICE_ASC_SORT_KEY]: Actions.Filters.SortingActivatedPriceAsc,
};

const StyledFilterAndSortStrip = styled.div`
    margin: 14px 0;
    color: ${({ theme }) => theme.colors.charcoal100};
    display: grid;
    grid-template:
        'sort filter' 20px
        / auto auto;
    ${forDesktop`
        margin: 10px 0 25px;
        grid-template:
            'filter sort' 1fr
            / auto minmax(120px, 181px);
    `}

    ${forIE`
        display: flex;
        flex-flow: row-reverse;
        justify-content: space-between;
    `}
`;

const SortingBox = styled(SortBox)`
    grid-area: sort;
    ${forDesktop`
        margin-top: 15px;
        align-items: normal;
    `}
`;

const DesktopFilterBox = styled(DesktopFiltersStrip)`
    grid-area: filter;
`;

export const StyledFilterAndSortButtons = styled.div`
    grid-area: filter;
    text-align: right;
    text-transform: capitalize;
    height: 100%;
    position: relative;
    display: flex;
    justify-content: flex-end;
    ${forDesktop`
        display: none;
    `}
`;

const StyledFilterAndSortIcons = styled(Svg)<{ width: string }>`
    display: inline-block;
    width: ${props => props.width}px;
    height: 100%;
    margin-right: 12px;
    margin-bottom: 4px;
    color: ${({ theme }) => theme.colors.charcoal100};
    vertical-align: bottom;
`;

export interface FilterAndSortBoxProps {
    handleFiltersClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    onSortValueChange: (newSortValue: SortingParameters) => void;
    sorting: ProductSorting[];
    value: SortingParameters;
}

const FilterAndSortBox: SFC<FilterAndSortBoxProps> = ({
    handleFiltersClick,
    onSortValueChange,
    value,
    sorting,
}): ReactElement => {
    const { location } = useRouter();
    const pageType = useTrackingPageLabel();
    const gaTracking = useGa();
    const t = useTranslator();
    const sendSortValueChange = (newSortValue: SortingParameters) => {
        const labelKey = `${newSortValue.sort}-${newSortValue.order}`;

        onSortValueChange(newSortValue);
        gaTracking.trackFilters({
            action: sortNameToGALabelMap[labelKey],
            label: pageType,
        });
    };

    const onSortOpen = () =>
        gaTracking.trackFilters({
            action: gaTracking.actions.Filters.SortingClick,
            label: pageType,
        });

    const onFiltersOpen = (event: MouseEvent<HTMLDivElement>) => {
        gaTracking.trackFilters({
            action: gaTracking.actions.Filters.MobileFilterClick,
            label: location.pathname,
        });

        handleFiltersClick(event);
    };

    return (
        <StyledFilterAndSortStrip>
            <SortingBox
                value={value}
                sorting={sorting}
                onSortOpen={onSortOpen}
                onSortValueChange={sendSortValueChange}
            />
            <StyledFilterAndSortButtons onClick={onFiltersOpen}>
                <StyledFilterAndSortIcons type="productFilter" width="20" />
                <StyledTextSpan textType="button">{t('Filter')}</StyledTextSpan>
            </StyledFilterAndSortButtons>
            <DesktopFilterBox />
        </StyledFilterAndSortStrip>
    );
};

export default FilterAndSortBox;
